import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiFacebook,
  FiInstagram,
  FiPhone,
  FiPhoneCall,
  FiMail,
  FiGlobe,
} from 'react-icons/fi';
import { SiTiktok } from 'react-icons/si';
import { getContact } from '../../../api/contactApi';
import { FooterOverlay, Newsletter } from '../../../components/ClientPage';
import { images } from '../../../constants';
import './Footer.css';
import { SOCIAL_MEDIA } from '../../../constants/enums';


const Footer = () => {
  const { t } = useTranslation();
  const [contact, setContact] = useState(null);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const data = await getContact();
        setContact(data);
      } catch (err) {
        console.error('Error fetching contact info:', err);
      }
    };
    fetchContact();
  }, []);

  const hasEmail = contact?.email && contact.email.trim() !== '';
  const hasWebsite = contact?.website && contact.website.trim() !== '';

  // Shorten website display (remove https://, www., trailing slash)
  const formatWebsiteName = (url) => {
    if (!url) return '';
    return url
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '');
  };

  return (
    <div className="app__footer section__padding" id="login">
      <FooterOverlay />
      <Newsletter />

      <div className="app__footer-links">
        {/* === Contact Info Section === */}
        <div className="app__footer-links_contact">
          <h1 className="app__footer-headtext">{t('footer.contact.title')}</h1>

          {/* Address */}
          <p className="p__opensans">
            {contact ? contact.address : t('footer.contact.loadingAddress')}
          </p>

          {/* Regular Phone (clickable) */}
          <p className="p__opensans" style={{ marginTop: '0.6rem' }}>
            <FiPhone style={{ color: '#DCCA87', marginRight: '8px' }} />
            <strong>Phone:</strong>{' '}
            {contact ? (
              <a
                href={`tel:${contact.phoneNumber}`}
                style={{
                  color: '#DCCA87',
                  textDecoration: 'none',
                }}
              >
                {contact.phoneNumber}
              </a>
            ) : (
              t('footer.contact.loadingPhone')
            )}
          </p>

          {/* Hotline (clickable) */}
          <p className="p__opensans" style={{ marginTop: '0.3rem' }}>
            <FiPhoneCall style={{ color: '#DCCA87', marginRight: '8px' }} />
            <strong>Hotline:</strong>{' '}
            {contact ? (
              <a
                href={`tel:${contact.hotline}`}
                style={{
                  color: '#DCCA87',
                  textDecoration: 'none',
                }}
              >
                {contact.hotline}
              </a>
            ) : (
              t('footer.contact.loadingHotline')
            )}
          </p>

          {/* Email (clickable) */}
          {hasEmail && (
            <p
              className="p__opensans"
              style={{
                fontSize: '0.9rem',
                marginTop: '0.5rem',
              }}
            >
              <FiMail style={{ color: '#DCCA87', marginRight: '8px' }} />
              <a
                href={`mailto:${contact.email}`}
                style={{
                  color: '#DCCA87',
                  textDecoration: 'none',
                  fontWeight: '400',
                }}
              >
                {contact.email}
              </a>
            </p>
          )}

          {/* Website (clickable & shortened) */}
          {hasWebsite && (
            <p
              className="p__opensans"
              style={{
                fontSize: '0.95rem',
                marginTop: '0.3rem',
                fontWeight: '500',
              }}
            >
              <FiGlobe style={{ color: '#DCCA87', marginRight: '8px' }} />
              <a
                href={
                  contact.website.startsWith('http')
                    ? contact.website
                    : `https://${contact.website}`
                }
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#DCCA87',
                  textDecoration: 'none',
                  textTransform: 'lowercase',
                }}
              >
                {formatWebsiteName(contact.website)}
              </a>
            </p>
          )}
        </div>

        {/* === Logo & Socials === */}
        <div className="app__footer-links_logo">
          <img src={images.pho1986} alt="footer_logo" />
          <p className="p__opensans">&quot;{t('footer.quote')}&quot;</p>
          <img
            src={images.spoon}
            alt="spoon"
            className="spoon__img"
            style={{ marginTop: 15 }}
          />

          {/* Social Media Icons */}
          <div className="app__footer-links_icons">
            <a
              href={SOCIAL_MEDIA.FACEBOOK}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <FiFacebook />
            </a>

            <a
              href={SOCIAL_MEDIA.INSTAGRAM}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <FiInstagram />
            </a>

            <a
              href={SOCIAL_MEDIA.TIKTOK}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
            >
              <SiTiktok />
            </a>
          </div>
        </div>

        {/* === Working Hours === */}
        <div className="app__footer-links_work">
          <h1 className="app__footer-headtext">
            {t('footer.workingHours.title')}
          </h1>
          <p className="p__opensans">{t('footer.workingHours.days')}</p>
          <p className="p__opensans">
            {contact
              ? contact.openingHours
              : t('footer.workingHours.loadingHours')}
          </p>
        </div>
      </div>

      {/* === Copyright === */}
      <div className="footer__copyright">
        <p className="p__opensans">
          2025 {contact ? contact.restaurantName : t('footer.loadingRestaurant')}
          . {t('footer.copyright')}
        </p>
      </div>
    </div>
  );
};

export default Footer;
